import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css";
import "./css/style.css";

import {Suspense, useCallback, useEffect, useState} from "react";
import {connect, useDispatch} from "react-redux";
import {Navigate, Route, Routes, useLocation, useNavigate, useParams} from "react-router-dom";
import {ToastContainer} from "react-toastify";

import Index from "./jsx";
import RedirectToDashboard from "./jsx/pages/RedirectToDashboard";
import LoadingSpinner from "./jsx/layouts/nav/LoadingSpinner";
import AppUpdateAvailableBanner from "./jsx/layouts/AppUpdateAvailableBanner";
import { GuidedTourProvider } from "./context/GuidedTourContext";
import { startAppVersionPoll } from "./services/appVersionPoll";
import ResetPassword from "./jsx/pages/ResetPassword";
import axiosInstance, { setNavigate } from "./services/AxiosInstance";
import {loginConfirmedAction} from "./store/actions/Auth/AuthActions";
import VerifyEmail from "./jsx/pages/VerifyEmail";
import lazyWithReload from "./utils/lazyWithReload";

const ForgotPassword = lazyWithReload(() => import("./jsx/pages/ForgotPassword"));
const Login = lazyWithReload(() => {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            import("./jsx/pages/Login").then(resolve, reject);
        }, 500);
    });
});

function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        let location = useLocation();
        let navigate = useNavigate();
        let params = useParams();

        return (
            <Component
                {...props}
                router={{location, navigate, params}}
            />
        );
    }

    return ComponentWithRouterProp;
}

function App(props) {
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [sessionCheckFailed, setSessionCheckFailed] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        setNavigate(props.router.navigate);
    }, [props.router.navigate]);

    useEffect(() => {
        startAppVersionPoll();
    }, []);

    const checkSession = useCallback(() => {
        setIsCheckingSession(true);
        setSessionCheckFailed(false);
        axiosInstance.get('/check-authentication')
            .then((response) => {
                const data = response.data;
                if (data?.authenticated) {
                    dispatch(loginConfirmedAction({user: data.user}));
                }
            })
            .catch(() => {
                setSessionCheckFailed(true);
            })
            .finally(() => {
                setIsCheckingSession(false);
            });
    }, [dispatch]);

    useEffect(() => {
        checkSession();
    }, [checkSession]);

    const unauthenticatedRoutes = (
        <Routes>
            <Route path="/login" element={<Login/>}/>
            <Route path="/forgot-password" element={<ForgotPassword/>}/>
            <Route path="/reset-password" element={<ResetPassword/>}/>
            <Route path="/verify-email" element={<VerifyEmail/>}/>
            <Route path="*" element={<Login/>}/>
        </Routes>
    );

    const authenticatedRoutes = (
        <Routes>
            <Route path="/" element={
                <RedirectToDashboard userRoleSlug={props.userRoleSlug}/>
            }/>
            <Route path="login" element={<Navigate to="/" replace/>}/>
            <Route path="/verify-email" element={<VerifyEmail/>}/>
            <Route path="/*" element={<Index/>}/>
        </Routes>
    );

    const authenticatedUnverifiedRoutes = (
        <Routes>
            <Route path="/verify-email" element={<VerifyEmail/>}/>
            <Route path="*" element={<Navigate to="/verify-email" replace/>}/>
        </Routes>
    );

    if (isCheckingSession || props.isLoading) {
        return <LoadingSpinner/>;
    }

    if (sessionCheckFailed) {
        return (
            <div className="authincation">
                <div className="container">
                    <div className="row justify-content-center h-100 align-items-center">
                        <div className="col-md-7">
                            <div className="form-input-content text-center error-page">
                                <h4>Unable to connect</h4>
                                <p>We couldn't reach the server. Check your connection and try again.</p>
                                <div>
                                    <button className="btn btn-primary" onClick={checkSession}>
                                        Try again
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // User is authenticated and email is verified.
    if (props.isAuthenticated && props.isAuthenticated.email_verified) {
        return (
            <GuidedTourProvider>
                <ToastContainer/>
                <AppUpdateAvailableBanner/>
                <Suspense fallback={<LoadingSpinner/>}>
                    {authenticatedRoutes}
                </Suspense>
            </GuidedTourProvider>
        );
    }

    // User is authenticated but email is not yet verified
    if (props.isAuthenticated && !props.isAuthenticated.email_verified) {
        return (
            <>
                <ToastContainer/>
                <AppUpdateAvailableBanner/>
                <Suspense fallback={<LoadingSpinner/>}>
                    {authenticatedUnverifiedRoutes}
                </Suspense>
            </>
        );
    }

    // User is not authenticated
    return (
        <>
            <ToastContainer/>
            <AppUpdateAvailableBanner/>
            <Suspense fallback={<LoadingSpinner/>}>
                {unauthenticatedRoutes}
            </Suspense>
        </>
    );
}

const mapStateToProps = (state) => {
    return {
        isAuthenticated: state.auth.authUser,
        isLoading: state.auth.showLoading,
        userRoleSlug: state.auth.authUser?.role_slug,
    };
};

export default withRouter(connect(mapStateToProps)(App));